import { ethers } from "ethers";

/**
 * Recovery v2 uses 12 English BIP-39 checksum words for 16-byte entropy.
 * Legacy v1 uses 24 words encoding the exact 32-byte spending key. Neither
 * format uses BIP-39 seed/HD derivation: computeSeed() changes the wallet.
 * Never ask for the recovery phrase of the user's public wallet here.
 */
export const RECOVERY_PHRASE_VERSION = 2;

export interface RecoveryWalletMaterial {
  spendingKey: Uint8Array;
  /** Present only for a v2 wallet; never derive this by truncating its key. */
  recoveryEntropy?: Uint8Array;
}

export interface RecoveryPhraseScope {
  /** Stable chain/network and pool scope, shared by export and restore. */
  scope: string;
  /** Canonical connected wallet identity (lowercase for EVM only). */
  wallet: string;
}

export interface RecoveryPhraseOptions {
  /** Must remain true for the same verified wallet/network connection. */
  isCurrent?: () => boolean;
}

export interface ResolveRecoveryWalletOptions extends RecoveryPhraseOptions {
  /** Only when the caller has explicitly selected the new-wallet derivation. */
  create12WordWallet?: boolean;
}

export interface RestoreRecoveryPhraseOptions extends RecoveryPhraseOptions {
  /** Explicit user restoration only; preserve corrupt bytes before replacing. */
  allowCorruptRecordReplacement?: boolean;
  /** Ask the user before an explicit restore changes a saved private identity. */
  confirmIdentityReplacement?: () => boolean | Promise<boolean>;
}

interface LegacyRecoveryRecord {
  v: 1;
  fingerprint: string;
  vault?: { iv: string; data: string };
}

interface EntropyRecoveryRecord {
  v: 2;
  kind: "entropy128";
  fingerprint: string;
  vault: { iv: string; data: string };
}

type RecoveryRecord = LegacyRecoveryRecord | EntropyRecoveryRecord;

export class RecoveryPhraseError extends Error {}

const PREFIX = "stealthos_recovery_phrase_v1:";
const PURPOSE = "StealthOS private-wallet recovery v1";
const ENTROPY_DOMAIN = "StealthOS private-wallet mnemonic entropy v2";
const SPENDING_KEY_DOMAIN = "StealthOS private-wallet spending key v2";
const RESTORE_ERROR =
  "This device could not unlock your original private wallet. Restore it with your StealthOS recovery phrase before continuing.";
const STORAGE_ERROR =
  "Private-wallet recovery could not access browser storage. Keep your recovery phrase and retry after enabling storage.";
const IDENTITY_ERROR =
  "This recovery phrase does not match the private wallet saved on this device. Use the original StealthOS recovery phrase.";
const INVALID_PHRASE =
  "Enter a valid 12-word or legacy 24-word StealthOS recovery phrase. Do not enter your MetaMask or Phantom recovery phrase.";

function assertCurrent(options: RecoveryPhraseOptions): void {
  if (options.isCurrent && !options.isCurrent())
    throw new RecoveryPhraseError(
      "Your wallet connection changed during recovery. Reconnect and retry.",
    );
}

function checkedKey(value: Uint8Array): Uint8Array {
  if (
    !(value instanceof Uint8Array) ||
    value.length !== 32 ||
    value.every((byte) => byte === 0)
  )
    throw new RecoveryPhraseError("Invalid StealthOS private-wallet key.");
  return new Uint8Array(value);
}

function checkedEntropy(value: Uint8Array): Uint8Array {
  if (
    !(value instanceof Uint8Array) ||
    value.length !== 16 ||
    value.every((byte) => byte === 0)
  )
    throw new RecoveryPhraseError("Invalid StealthOS recovery entropy.");
  return new Uint8Array(value);
}

function materialFromEntropy(value: Uint8Array): RecoveryWalletMaterial {
  const recoveryEntropy = checkedEntropy(value);
  // Exact v2 key derivation: SHA256(UTF8(SPENDING_KEY_DOMAIN) || entropy16).
  const spendingKey = ethers.getBytes(
    ethers.sha256(
      ethers.concat([ethers.toUtf8Bytes(SPENDING_KEY_DOMAIN), recoveryEntropy]),
    ),
  );
  return { spendingKey: checkedKey(spendingKey), recoveryEntropy };
}

function checkedMaterial(
  material: RecoveryWalletMaterial,
): RecoveryWalletMaterial {
  const spendingKey = checkedKey(material.spendingKey);
  if (material.recoveryEntropy === undefined) return { spendingKey };
  const recovered = materialFromEntropy(material.recoveryEntropy);
  if (ethers.hexlify(recovered.spendingKey) !== ethers.hexlify(spendingKey))
    throw new RecoveryPhraseError(
      "The recovery phrase does not belong to this private wallet.",
    );
  return recovered;
}

function checkedSignature(signature: Uint8Array): Uint8Array {
  if (
    !(signature instanceof Uint8Array) ||
    ![64, 65].includes(signature.length) ||
    signature.every((byte) => byte === 0)
  )
    throw new RecoveryPhraseError(RESTORE_ERROR);
  return new Uint8Array(signature);
}

/** Exact v2 entropy derivation: first16(SHA256(UTF8(ENTROPY_DOMAIN) || signature)). */
export function deriveNewRecoveryWallet(
  walletSignature: Uint8Array,
): RecoveryWalletMaterial {
  const entropy = ethers
    .getBytes(
      ethers.sha256(
        ethers.concat([
          ethers.toUtf8Bytes(ENTROPY_DOMAIN),
          checkedSignature(walletSignature),
        ]),
      ),
    )
    .slice(0, 16);
  return materialFromEntropy(entropy);
}

export function spendingKeyToRecoveryPhrase(
  key: Uint8Array,
  recoveryEntropy?: Uint8Array,
): string {
  const material = checkedMaterial({ spendingKey: key, recoveryEntropy });
  return ethers.Mnemonic.fromEntropy(
    material.recoveryEntropy ?? material.spendingKey,
    "",
    ethers.wordlists.en,
  ).phrase;
}

export function recoveryPhraseToWalletMaterial(
  phrase: string,
): RecoveryWalletMaterial {
  try {
    if (typeof phrase !== "string") throw new Error();
    const normalized = phrase.trim().toLowerCase().split(/\s+/).join(" ");
    const words = normalized.split(" ").length;
    if (words !== 12 && words !== 24) throw new Error();
    const entropy = ethers.getBytes(
      ethers.Mnemonic.fromPhrase(normalized, "", ethers.wordlists.en).entropy,
    );
    return words === 12
      ? materialFromEntropy(entropy)
      : { spendingKey: checkedKey(entropy) };
  } catch {
    // ethers errors can include the invalid phrase: do not pass them through.
    throw new RecoveryPhraseError(INVALID_PHRASE);
  }
}

export function recoveryPhraseToSpendingKey(phrase: string): Uint8Array {
  return recoveryPhraseToWalletMaterial(phrase).spendingKey;
}

function scopeParts(target: RecoveryPhraseScope): [string, string] {
  if (
    !target ||
    typeof target.scope !== "string" ||
    typeof target.wallet !== "string" ||
    !target.scope.trim() ||
    !target.wallet.trim()
  )
    throw new RecoveryPhraseError("Private-wallet recovery scope is missing.");
  return [target.scope, target.wallet];
}

function recordKey(target: RecoveryPhraseScope): string {
  return PREFIX + scopeParts(target).map(encodeURIComponent).join(":");
}

function readRaw(target: RecoveryPhraseScope): string | null {
  const key = recordKey(target);
  try {
    return window.localStorage.getItem(key);
  } catch {
    throw new RecoveryPhraseError(STORAGE_ERROR);
  }
}

function parseRecord(raw: string | null): RecoveryRecord | null {
  if (raw === null) return null;
  try {
    const value = JSON.parse(raw);
    if (
      ![1, 2].includes(value?.v) ||
      typeof value?.fingerprint !== "string" ||
      !/^0x[0-9a-f]{64}$/.test(value.fingerprint) ||
      (value.v === 2 &&
        (value.kind !== "entropy128" || value.vault === undefined)) ||
      (value.vault !== undefined &&
        (typeof value.vault?.iv !== "string" ||
          !/^0x[0-9a-f]{24}$/.test(value.vault.iv) ||
          typeof value.vault?.data !== "string" ||
          !(value.v === 1 ? /^0x[0-9a-f]{96}$/ : /^0x[0-9a-f]{64}$/).test(
            value.vault.data,
          )))
    )
      throw new Error();
    return value as RecoveryRecord;
  } catch {
    // A corrupt/unsupported record must not turn into a new private wallet.
    throw new RecoveryPhraseError(RESTORE_ERROR);
  }
}

function fingerprint(key: Uint8Array): string {
  return ethers.keccak256(
    ethers.concat([
      ethers.toUtf8Bytes(`${PURPOSE}: identity`),
      checkedKey(key),
    ]),
  );
}

function writeRecord(
  target: RecoveryPhraseScope,
  previous: string | null,
  record: RecoveryRecord,
  preservePrevious = false,
): void {
  // Crypto awaits allow another tab/connection to update storage. Refuse to
  // replace that record. Identity and encrypted key are a single atomic write.
  if (readRaw(target) !== previous)
    throw new RecoveryPhraseError(
      "Your private-wallet recovery settings changed. Reconnect and retry.",
    );
  const key = recordKey(target);
  const raw = JSON.stringify(record);
  try {
    if (preservePrevious && previous !== null) {
      // A corrupt record or explicitly replaced identity is not disposable.
      // Keep each distinct record under an idempotent quarantine key.
      if (previous.length > 16384) throw new Error();
      const quarantineKey = `${key}:quarantine:${ethers.keccak256(
        ethers.toUtf8Bytes(previous),
      )}`;
      const saved = window.localStorage.getItem(quarantineKey);
      if (saved !== null && saved !== previous) throw new Error();
      window.localStorage.setItem(quarantineKey, previous);
      if (window.localStorage.getItem(quarantineKey) !== previous)
        throw new Error();
    }
    window.localStorage.setItem(key, raw);
    if (window.localStorage.getItem(key) !== raw) throw new Error();
  } catch {
    throw new RecoveryPhraseError(STORAGE_ERROR);
  }
}

/** A corrupt record requires explicit restoration, never the legacy fallback. */
export function hasRecoveryKeyOverride(target: RecoveryPhraseScope): boolean {
  return parseRecord(readRaw(target))?.vault !== undefined;
}

export function hasStoredRecoveryWallet(target: RecoveryPhraseScope): boolean {
  return parseRecord(readRaw(target)) !== null;
}

/** Pin only a non-secret identity; never persist a plaintext key or phrase. */
export function pinRecoverySpendingKey(
  target: RecoveryPhraseScope,
  key: Uint8Array,
): void {
  const currentFingerprint = fingerprint(key);
  const previous = readRaw(target);
  const record = parseRecord(previous);
  if (record) {
    if (record.fingerprint !== currentFingerprint)
      throw new RecoveryPhraseError(RESTORE_ERROR);
    return;
  }
  writeRecord(target, previous, {
    v: 1,
    fingerprint: currentFingerprint,
  });
}

function asBuffer(bytes: Uint8Array): ArrayBuffer {
  return new Uint8Array(bytes).buffer;
}

async function wrappingKey(
  target: RecoveryPhraseScope,
  signature: Uint8Array,
): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    "raw",
    asBuffer(checkedSignature(signature)),
    "HKDF",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: asBuffer(ethers.toUtf8Bytes(`${PURPOSE}: wrapping`)),
      info: asBuffer(ethers.toUtf8Bytes(JSON.stringify(scopeParts(target)))),
    },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

function associatedData(
  target: RecoveryPhraseScope,
  identity: string,
  version: 1 | 2 = 1,
): ArrayBuffer {
  return asBuffer(
    ethers.toUtf8Bytes(
      JSON.stringify(
        version === 1
          ? [PURPOSE, ...scopeParts(target), identity]
          : [
              "StealthOS private-wallet recovery",
              2,
              "entropy128",
              ...scopeParts(target),
              identity,
            ],
      ),
    ),
  );
}

/**
 * Resolve a wallet-derived candidate without changing any saved identity.
 * A missing record is not evidence of a new wallet: the caller must distinguish
 * Create and Restore on a clean device, and pin only after connection succeeds.
 */
export async function resolveRecoveryWallet(
  target: RecoveryPhraseScope,
  walletSignature: Uint8Array,
  candidateKey: Uint8Array,
  options: ResolveRecoveryWalletOptions = {},
): Promise<RecoveryWalletMaterial> {
  assertCurrent(options);
  const record = parseRecord(readRaw(target));
  const candidate = checkedKey(candidateKey);
  if (!record)
    return options.create12WordWallet
      ? deriveNewRecoveryWallet(walletSignature)
      : { spendingKey: candidate };
  if (!record.vault) {
    if (record.fingerprint !== fingerprint(candidate))
      throw new RecoveryPhraseError(RESTORE_ERROR);
    return { spendingKey: candidate };
  }
  let recovered: RecoveryWalletMaterial;
  try {
    const plaintext = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: asBuffer(ethers.getBytes(record.vault.iv)),
        additionalData: associatedData(target, record.fingerprint, record.v),
      },
      await wrappingKey(target, walletSignature),
      asBuffer(ethers.getBytes(record.vault.data)),
    );
    recovered =
      record.v === 2
        ? materialFromEntropy(new Uint8Array(plaintext))
        : { spendingKey: checkedKey(new Uint8Array(plaintext)) };
    if (fingerprint(recovered.spendingKey) !== record.fingerprint)
      throw new Error();
  } catch {
    throw new RecoveryPhraseError(RESTORE_ERROR);
  }
  assertCurrent(options);
  return recovered;
}

export async function resolveRecoverySpendingKey(
  target: RecoveryPhraseScope,
  walletSignature: Uint8Array,
  candidateKey: Uint8Array,
  options: RecoveryPhraseOptions = {},
): Promise<Uint8Array> {
  return (
    await resolveRecoveryWallet(target, walletSignature, candidateKey, options)
  ).spendingKey;
}

async function encryptedRecord(
  target: RecoveryPhraseScope,
  walletSignature: Uint8Array,
  material: RecoveryWalletMaterial,
): Promise<RecoveryRecord> {
  const identity = fingerprint(material.spendingKey);
  const version = material.recoveryEntropy === undefined ? 1 : 2;
  try {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: asBuffer(iv),
        additionalData: associatedData(target, identity, version),
      },
      await wrappingKey(target, walletSignature),
      asBuffer(material.recoveryEntropy ?? material.spendingKey),
    );
    const vault = {
      iv: ethers.hexlify(iv),
      data: ethers.hexlify(new Uint8Array(ciphertext)),
    };
    return version === 2
      ? { v: 2, kind: "entropy128", fingerprint: identity, vault }
      : { v: 1, fingerprint: identity, vault };
  } catch {
    throw new RecoveryPhraseError(RESTORE_ERROR);
  }
}

/** Save only after final account/chain checks; matching stored identities are left intact. */
export async function persistRecoveryWallet(
  target: RecoveryPhraseScope,
  walletSignature: Uint8Array,
  value: RecoveryWalletMaterial,
  options: RecoveryPhraseOptions = {},
): Promise<void> {
  assertCurrent(options);
  const material = checkedMaterial(value);
  const previous = readRaw(target);
  const record = parseRecord(previous);
  const identity = fingerprint(material.spendingKey);
  if (record) {
    if (record.fingerprint !== identity)
      throw new RecoveryPhraseError(RESTORE_ERROR);
    return;
  }
  const next: RecoveryRecord =
    material.recoveryEntropy === undefined
      ? { v: 1, fingerprint: identity }
      : await encryptedRecord(target, walletSignature, material);
  assertCurrent(options);
  writeRecord(target, previous, next);
}

/**
 * Restore the exact original key, then remember it encrypted for this wallet's
 * current signature. The phrase remains the independent recovery method if
 * that signature or device changes again. No plaintext secret is persisted.
 */
export async function restoreRecoveryPhrase(
  target: RecoveryPhraseScope,
  walletSignature: Uint8Array,
  phrase: string,
  options: RestoreRecoveryPhraseOptions = {},
): Promise<Uint8Array> {
  assertCurrent(options);
  const restored = recoveryPhraseToWalletMaterial(phrase);
  const identity = fingerprint(restored.spendingKey);
  const previous = readRaw(target);
  let savedFingerprint: unknown;
  let preservePrevious = false;
  try {
    savedFingerprint = parseRecord(previous)?.fingerprint;
  } catch (error) {
    if (!options.allowCorruptRecordReplacement) throw error;
    // An unreadable vault may still contain an intact identity, which needs
    // the same explicit switch confirmation as any other saved identity.
    try {
      savedFingerprint = JSON.parse(previous!).fingerprint;
    } catch {}
    preservePrevious = true;
  }
  if (
    typeof savedFingerprint === "string" &&
    /^0x[0-9a-f]{64}$/.test(savedFingerprint) &&
    savedFingerprint !== identity
  ) {
    const confirmed = await options.confirmIdentityReplacement?.();
    assertCurrent(options);
    if (!confirmed) throw new RecoveryPhraseError(IDENTITY_ERROR);
    if (readRaw(target) !== previous)
      throw new RecoveryPhraseError(
        "Your private-wallet recovery settings changed. Reconnect and retry.",
      );
    preservePrevious = true;
  }
  const record = await encryptedRecord(target, walletSignature, restored);
  assertCurrent(options);
  writeRecord(target, previous, record, preservePrevious);
  return restored.spendingKey;
}
